import React, { useCallback, useDeferredValue, useState, useEffect, useRef, useMemo } from 'react';
import FieldMap from './FieldMap';
import { calculatePolygonArea, parseKmlFile, reverseGeocodeComuna, cleanComunaName, canonicalComuna } from '../services/mapService';
import universalDataService from '../services/universalDataService';
import { contentKey } from '../services/contentKey';
import { splitField } from './fieldSplit';
import './FieldsPage.css';

// ── Constants ─────────────────────────────────────────────────────────────────

const cropTypes = [
  { value: 'wheat',     label: 'Grâu',              color: '#E8A838' }, // amber gold
  { value: 'corn',      label: 'Porumb',             color: '#3B82F6' }, // vibrant blue
  { value: 'sunflower', label: 'Floarea-soarelui',   color: '#FACC15' }, // bright yellow
  { value: 'rapeseed',  label: 'Rapiță',             color: '#84CC16' }, // yellow-green
  { value: 'alfalfa',   label: 'Lucernă',            color: '#A855F7' }, // purple
  { value: 'soybean',   label: 'Soia',               color: '#F97316' }, // orange
  { value: 'fallow',    label: 'Ogor',               color: '#94A3B8' }, // slate gray
];

// Agriculture year: Oct-Sep. e.g. March 2026 → "2025-2026"
const getCurrentAgriYear = () => {
  const m = new Date().getMonth(); // 0=Jan, 9=Oct
  const y = new Date().getFullYear();
  return m >= 9 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
};

// Normalize T number: strip "T" prefix → "63" not "T63"
const normT = (t) => String(t || '').replace(/^T/i, '').trim();

// Normalize Comună for comparison: strip "Comuna/Oraș/…" prefix, lowercase, remove diacritics.
// This ensures "Mircești", "Comuna Mircești" and "Mircesti" all hash to the same key.
const normC = (s) =>
  canonicalComuna(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

// Get year-specific field data (with root fallback for backward compat)
const getFieldYearData = (field, year) => {
  const yd = field.yearData?.[year] || {};
  return {
    crop:        yd.crop        ?? field.crop        ?? '',
    cropType:    yd.cropType    ?? field.cropType    ?? '',
    stage:       yd.stage       ?? field.stage       ?? '',
    health:      yd.health      ?? field.health      ?? 'Good',
    contract:    yd.contract    ?? field.contract    ?? '',
    coordinates: yd.coordinates ?? null,
  };
};

// Stored values are English; the screen shows Romanian.
const DISPLAY = {
  Planting: 'Plantare', Growing: 'Creștere', Harvesting: 'Recoltare', Harvested: 'Recoltat',
  Excellent: 'Excelent', Good: 'Bun', Fair: 'Acceptabil', Poor: 'Slab',
};

// A callback that never changes identity but always runs the latest code.
const useEvent = (fn) => {
  const latest = useRef(fn);
  latest.current = fn;
  return useCallback((...args) => latest.current(...args), []);
};

const Icon = ({ d }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinejoin="round" strokeLinecap="round" aria-hidden="true"><path d={d} /></svg>
);

// ── Component ─────────────────────────────────────────────────────────────────

const Fields = ({ dataService = universalDataService, onMapReady }) => {
  const [fields,           setFields]           = useState([]);

  // Data arriving from a sync only replaces the list if it really changed, and loaded
  // data isn't written straight back. This stops sync updates redrawing the whole map.
  const applyLoadedFields = (next) => setFields((prev) => {
    const result = contentKey(prev) === contentKey(next) ? prev : next;
    lastLoadedFields.current = result;
    return result;
  });
  const [selectedField,    setSelectedField]    = useState(null);
  const [searchTerm,       setSearchTerm]       = useState('');
  const [showFieldForm,    setShowFieldForm]    = useState(false);
  const [newFieldCoords,   setNewFieldCoords]   = useState(null);
  const [importedPolygons, setImportedPolygons] = useState([]);
  const [mapInstance,      setMapInstance]      = useState(null);
  const [selectedFields,   setSelectedFields]   = useState([]);
  const [batchMode,        setBatchMode]        = useState(false);
  const [sortConfig,       setSortConfig]       = useState({ key: 'name', direction: 'ascending' });
  const [filterCrop,       setFilterCrop]       = useState('');
  const [filterStage,      setFilterStage]      = useState('');
  const [filterHealth,     setFilterHealth]     = useState('');
  const [filterComuna,     setFilterComuna]     = useState('');
  const [parcelsData,      setParcelsData]      = useState([]);
  const [detectingComune,  setDetectingComune]  = useState(false);
  const [comunaDetection,  setComunaDetection]  = useState('idle');

  // Agriculture year
  const [selectedYear,   setSelectedYear]   = useState(getCurrentAgriYear());
  const [availableYears, setAvailableYears] = useState([getCurrentAgriYear()]);
  const [newYearInput,   setNewYearInput]   = useState('');
  const [showAddYear,    setShowAddYear]    = useState(false);

  // Company
  const [companies, setCompanies] = useState([]);

  // Polygon shape editing
  const [editingFieldId,    setEditingFieldId]    = useState(null);
  const [session,           setSession]           = useState(null);
  const [sheet,             setSheet]             = useState('peek');
  const [panelTab,          setPanelTab]          = useState('list');

  // Polygon split
  const [splitMode,        setSplitMode]        = useState(false);
  const [splitSourceField, setSplitSourceField] = useState(null);

  // Form state (year-independent: name, area, tNumber, comuna, company)
  // Year-specific: crop, cropType, stage, health, contract
  const [fieldForm, setFieldForm] = useState({
    name: '', area: '', tNumber: '', comuna: '', company: '',
    crop: '', cropType: '', stage: '', health: 'Good', contract: '',
    apiaOnly: false, folosinta: 'arabil'
  });

  const fileInputRef         = useRef(null);
  const mapRef               = useRef(null);
  const sheetRef             = useRef(null);
  const lastLoadedFields     = useRef(null);
  const migrationDone        = useRef(false);
  const comunaMigrationDone  = useRef(false);

  // ── Load & Sync ──────────────────────────────────────────────────────────────

  useEffect(() => {
    // ── Load fields via universalDataService (synced to Firebase) ─────────────
    //
    // Three-tier loading strategy:
    //   1. dataService.loadFields()  — reads fieldsData_{farmId} from localStorage
    //   2. Old agrimanage-fields key          — migration for pre-sync data
    //   3. Direct Firebase read               — guaranteed fallback for new devices / cleared cache
    //
    const loadFieldsFromCloud = async () => {
      const farmId = localStorage.getItem('currentFarmId');
      if (!farmId) return null;
      return dataService.loadFromCloud(farmId, 'fieldsData').catch(() => null);
    };

    const loadFieldsData = async () => {
      // 1. Try the synced localStorage store (fastest path)
      const synced = await dataService.loadFields().catch(() => null);
      if (synced && synced.length > 0) {
        applyLoadedFields(synced);
        return;
      }

      // 2. Fall back to the old localStorage key and migrate it
      const oldRaw = localStorage.getItem('agrimanage-fields');
      if (oldRaw) {
        try {
          const parsed = JSON.parse(oldRaw);
          const oldFields = parsed.data || parsed;
          if (Array.isArray(oldFields) && oldFields.length > 0) {
            setFields(oldFields);
            // Migrate: push to Firebase so other devices can sync
            dataService.saveFields(oldFields).catch(() => {});
            return;
          }
        } catch {}
      }

      // 3. Direct Firebase read — for new devices, cleared cache, or session timing issues
      const cloudData = await loadFieldsFromCloud();
      if (cloudData && cloudData.length > 0) {
        applyLoadedFields(cloudData);
        // Cache locally for future loads (non-blocking — OK if session not ready yet)
        dataService.saveFields(cloudData).catch(() => {});
      }
    };
    loadFieldsData();

    // Imported polygons — local only (converted to real fields before sync)
    const savedImports = localStorage.getItem('agrimanage-imported');
    if (savedImports) { const p = JSON.parse(savedImports); setImportedPolygons(p.data || p); }

    // Reload fields when cloud sync brings new data from Firebase
    const reloadFields = () => setTimeout(async () => {
      // Try localStorage first (might now be populated after a sync event)
      const latest = await dataService.loadFields().catch(() => null);
      if (latest && latest.length > 0) {
        applyLoadedFields(latest);
      } else {
        // Fallback: read directly from Firebase (catches session-not-ready edge cases)
        const cloudData = await loadFieldsFromCloud();
        if (cloudData && cloudData.length > 0) {
          applyLoadedFields(cloudData);
          dataService.saveFields(cloudData).catch(() => {});
        }
      }
      // Imported polygons stay local-only
      const si = localStorage.getItem('agrimanage-imported');
      if (si) { const p = JSON.parse(si); setImportedPolygons(p.data || p); }
    }, 50);

    window.addEventListener('cloudSyncComplete', reloadFields);
    window.addEventListener('fieldsDataUpdated', reloadFields);

    dataService.loadParcels()
      .then(d => { if (Array.isArray(d)) setParcelsData(d); })
      .catch(() => {});

    const reloadParcels = () => dataService.loadParcels()
      .then(d => { if (Array.isArray(d)) setParcelsData(d); }).catch(() => {});
    window.addEventListener('parcelDataUpdated', reloadParcels);

    dataService.loadCompanySettings()
      .then(s => {
        const list = s?.companies || [];
        setCompanies(list.map(c => (typeof c === 'string' ? c : c.name || String(c))));
      }).catch(() => {});

    return () => {
      window.removeEventListener('cloudSyncComplete', reloadFields);
      window.removeEventListener('fieldsDataUpdated', reloadFields);
      window.removeEventListener('parcelDataUpdated', reloadParcels);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Build available years from fields data
  useEffect(() => {
    const s = new Set([getCurrentAgriYear()]);
    fields.forEach(f => f.yearData && Object.keys(f.yearData).forEach(y => s.add(y)));
    setAvailableYears(Array.from(s).sort().reverse());
  }, [fields]);

  // One-time migration: fields without yearData get their root crop/stage/health moved to prev year
  useEffect(() => {
    if (migrationDone.current || fields.length === 0) return;
    if (!fields.some(f => !f.yearData)) { migrationDone.current = true; return; }
    migrationDone.current = true;
    const prevYear = (() => {
      const m = new Date().getMonth(), y = new Date().getFullYear();
      return m >= 9 ? `${y - 1}-${y}` : `${y - 2}-${y - 1}`;
    })();
    setFields(prev => prev.map(f => {
      if (f.yearData) return f;
      const yd = {};
      if (f.crop || f.stage || f.health || f.contract) {
        yd[prevYear] = { crop: f.crop||'', cropType: f.cropType||'', stage: f.stage||'', health: f.health||'Good', contract: f.contract||'' };
      }
      return { ...f, yearData: yd };
    }));
  }, [fields]);

  // One-time migration: strip "Comuna " / "Oraș " prefix from stored f.comuna values so
  // that old data ("Comuna Mircești") and new data ("Mircești") resolve to the same name.
  useEffect(() => {
    if (comunaMigrationDone.current || fields.length === 0) return;
    comunaMigrationDone.current = true;
    const needsFix = fields.some(f => f.comuna && f.comuna !== cleanComunaName(f.comuna));
    if (!needsFix) return;
    setFields(prev => prev.map(f =>
      f.comuna ? { ...f, comuna: cleanComunaName(f.comuna) } : f
    ));
  }, [fields]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist fields via universalDataService → syncs to Firebase automatically
  useEffect(() => {
    if (fields.length > 0 && fields !== lastLoadedFields.current) {
      dataService.saveFields(fields).catch(() => {});
    }
  }, [fields]);

  useEffect(() => {
    if (importedPolygons.length > 0) localStorage.setItem('agrimanage-imported', JSON.stringify(importedPolygons));
  }, [importedPolygons]);

  // ── Year ──────────────────────────────────────────────────────────────────────

  const handleAddYear = () => {
    const y = newYearInput.trim();
    if (!y) return;
    if (!availableYears.includes(y)) setAvailableYears(prev => [y, ...prev].sort().reverse());
    setSelectedYear(y);
    setNewYearInput('');
    setShowAddYear(false);
  };

  // ── Map fields — inject year-specific cropType + coordinates so the map always
  //    shows the correct colour and shape for the active year ──────────────────

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fieldsForMap = useMemo(() => fields.map(f => {
    const yd = getFieldYearData(f, selectedYear);
    return {
      ...f,
      cropType:    yd.cropType || '',
      coordinates: (yd.coordinates && yd.coordinates.length >= 3) ? yd.coordinates : f.coordinates,
    };
  }), [fields, selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sorting & Filtering ───────────────────────────────────────────────────────

  const requestSort = (key) => setSortConfig(prev => ({
    key,
    direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending'
  }));

  const getSortIndicator = (key) => sortConfig.key === key ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓') : '';

  const deferredSearch = useDeferredValue(searchTerm);
  const filteredFields = useMemo(() => {
    const st = deferredSearch.toLowerCase();
    const yearFields = fields.map(f => ({ ...f, _yd: getFieldYearData(f, selectedYear) }));

    let filtered = yearFields.filter(f =>
      f.name.toLowerCase().includes(st) ||
      (f._yd.crop && f._yd.crop.toLowerCase().includes(st)) ||
      (f._yd.contract && f._yd.contract.toLowerCase().includes(st))
    );

    if (filterCrop)   filtered = filtered.filter(f => f._yd.cropType === filterCrop);
    if (filterStage)  filtered = filtered.filter(f => f._yd.stage    === filterStage);
    if (filterHealth) filtered = filtered.filter(f => f._yd.health   === filterHealth);
    if (filterComuna) filtered = filtered.filter(f => normC(f.comuna) === normC(filterComuna));

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const getV = (f) => {
          if (sortConfig.key === 'area') return parseFloat(f.area) || 0;
          if (['crop','cropType','stage','health','contract'].includes(sortConfig.key))
            return (f._yd[sortConfig.key] || '').toLowerCase();
          const v = f[sortConfig.key];
          return typeof v === 'string' ? v.toLowerCase() : (v ?? '');
        };
        const av = getV(a), bv = getV(b);
        if (!av && !bv) return 0; if (!av) return 1; if (!bv) return -1;
        return sortConfig.direction === 'ascending' ? (av < bv ? -1 : av > bv ? 1 : 0) : (av > bv ? -1 : av < bv ? 1 : 0);
      });
    }
    return filtered.map(({ _yd, ...rest }) => rest); // eslint-disable-line no-unused-vars
  }, [fields, selectedYear, deferredSearch, filterCrop, filterStage, filterHealth, filterComuna, sortConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const stageOptions  = [...new Set(fields.map(f => getFieldYearData(f, selectedYear).stage).filter(Boolean))].sort();
  const healthOptions = [...new Set(fields.map(f => getFieldYearData(f, selectedYear).health).filter(Boolean))].sort();

  // Hectares per crop type for the selected year (only worked fields, not APIA-only)
  const cropHa = useMemo(() => {
    const ha = {};
    fields.forEach(f => {
      if (f.apiaOnly) return;
      const ct = getFieldYearData(f, selectedYear).cropType;
      if (!ct) return;
      ha[ct] = (ha[ct] || 0) + (parseFloat(f.area) || 0);
    });
    return ha;
  }, [fields, selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearFilters = () => { setSearchTerm(''); setFilterCrop(''); setFilterStage(''); setFilterHealth(''); setFilterComuna(''); };
  const toggleSelectAllFields = () => setSelectedFields(prev => prev.length === filteredFields.length ? [] : [...filteredFields]);

  // ── Field Handlers ────────────────────────────────────────────────────────────

  const handleFieldSelect = (field) => {
    if (batchMode) {
      setSelectedFields(prev => prev.some(f => f.id === field.id) ? prev.filter(f => f.id !== field.id) : [...prev, field]);
    } else {
      setSelectedField(field);
    }
  };

  const handleDrawField = async (coordinates) => {
    const area = calculatePolygonArea(coordinates);
    const lat  = coordinates.reduce((s, p) => s + p.lat, 0) / coordinates.length;
    const lng  = coordinates.reduce((s, p) => s + p.lng, 0) / coordinates.length;
    setNewFieldCoords(coordinates);
    setFieldForm({ name: `Poligon ${fields.length + 1}`, area: area.toFixed(2), tNumber: '', comuna: '', company: '', crop: '', cropType: '', stage: '', health: 'Good', contract: '', apiaOnly: false, folosinta: 'arabil' });
    setShowFieldForm(true);
    setComunaDetection('searching');
    try {
      const detected = await reverseGeocodeComuna(lat, lng);
      if (detected) setFieldForm(prev => ({ ...prev, comuna: prev.comuna || detected }));
      setComunaDetection(detected ? 'found' : 'failed');
    } catch (e) {
      setComunaDetection('failed');
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'cropType') {
      const ct = cropTypes.find(c => c.value === value);
      setFieldForm(prev => ({ ...prev, cropType: value, crop: ct ? ct.label : '' }));
    } else if (type === 'checkbox') {
      setFieldForm(prev => ({ ...prev, [name]: checked }));
    } else {
      setFieldForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveField = () => {
    const { name, area, tNumber, comuna, company, crop, cropType, stage, health, contract, apiaOnly, folosinta } = fieldForm;
    const yearSpecific = { crop, cropType, stage, health, contract };
    if (newFieldCoords) {
      const nf = {
        id: Date.now(), name, area, tNumber, comuna, company,
        apiaOnly: !!apiaOnly, folosinta: folosinta || 'arabil',
        coordinates: newFieldCoords,
        yearData: { [selectedYear]: { ...yearSpecific, coordinates: newFieldCoords } }
      };
      setFields(prev => [...prev, nf]);
      setSelectedField(nf);
      if (selectedField?.imported) {
        // A converted KML polygon stops being an imported one
        const remaining = importedPolygons.filter(p => p.id !== selectedField.id);
        setImportedPolygons(remaining);
        if (!remaining.length) localStorage.removeItem('agrimanage-imported');
      }
    } else if (selectedField) {
      setFields(prev => prev.map(f => f.id !== selectedField.id ? f : {
        ...f, name, tNumber, comuna, company,
        apiaOnly: !!apiaOnly, folosinta: folosinta || 'arabil',
        yearData: { ...(f.yearData||{}), [selectedYear]: { ...(f.yearData?.[selectedYear]||{}), ...yearSpecific } }
      }));
      setSelectedField(prev => ({ ...prev, name, tNumber, comuna, company, apiaOnly: !!apiaOnly, folosinta: folosinta || 'arabil' }));
    }
    setShowFieldForm(false);
    setNewFieldCoords(null);
  };

  const handleCancelField = () => { setShowFieldForm(false); setNewFieldCoords(null); };

  const handleEditField = (field) => {
    const yd = getFieldYearData(field, selectedYear);
    setSelectedField(field);
    setFieldForm({
      name: field.name, area: field.area, tNumber: field.tNumber||'',
      comuna: field.comuna||'', company: field.company||'',
      apiaOnly: field.apiaOnly||false, folosinta: field.folosinta||'arabil',
      crop: yd.crop, cropType: yd.cropType, stage: yd.stage, health: yd.health, contract: yd.contract
    });
    setShowFieldForm(true);
  };

  const handleDeleteField = (fieldId) => {
    if (window.confirm('Ștergeți câmpul selectat?')) {
      setFields(prev => prev.filter(f => f.id !== fieldId));
      if (selectedField?.id === fieldId) setSelectedField(null);
    }
  };

  const handleBatchDelete = () => {
    if (!selectedFields.length) { alert('Selectați cel puțin un câmp.'); return; }
    if (window.confirm(`Ștergeți ${selectedFields.length} câmpuri selectate?`)) {
      const ids = new Set(selectedFields.map(f => f.id));
      setFields(prev => prev.filter(f => !ids.has(f.id)));
      setSelectedFields([]); setSelectedField(null); setBatchMode(false);
    }
  };

  const toggleBatchMode = () => { setBatchMode(p => !p); setSelectedFields([]); };

  // ── Shape Editing ─────────────────────────────────────────────────────────────

  const handleStartEditShape = (field) => {
    setEditingFieldId(field.id);
    mapRef.current?.focusField(field);
    mapRef.current?.startSession('shape', field.coordinates);
  };

  const handleSaveShape = () => {
    const editedCoords = session?.dirty ? mapRef.current?.getSessionPath() : null;
    mapRef.current?.endSession();
    if (!editingFieldId || !editedCoords || editedCoords.length < 3) { setEditingFieldId(null); return; }
    const area = calculatePolygonArea(editedCoords);
    setFields(prev => prev.map(f => f.id !== editingFieldId ? f : {
      ...f, coordinates: editedCoords, area: area.toFixed(2),
      yearData: { ...(f.yearData||{}), [selectedYear]: { ...(f.yearData?.[selectedYear]||{}), coordinates: editedCoords } }
    }));
    setEditingFieldId(null);
  };

  // ── Polygon Split ─────────────────────────────────────────────────────────────

  const handleSplitDrawComplete = (drawnCoords) => {
    setSplitMode(false);
    if (!splitSourceField || drawnCoords.length < 3) { setSplitSourceField(null); return; }
    const origCoords = splitSourceField.coordinates;
    if (!origCoords || origCoords.length < 3) { setSplitSourceField(null); return; }
    try {
      const pieces = splitField(splitSourceField, drawnCoords, selectedYear, calculatePolygonArea);
      if (!pieces) {
        alert('Nu s-a putut efectua împărțirea.\nAsigurați-vă că poligonul desenat se suprapune cu câmpul existent.');
        setSplitSourceField(null); return;
      }
      setFields(prev => prev.filter(f => f.id !== splitSourceField.id).concat(pieces));
      setSplitSourceField(null);
    } catch (err) {
      console.error('Split error:', err);
      alert('Eroare la împărțire. Verificați dacă poligoanele se suprapun corect.');
      setSplitSourceField(null);
    }
  };

  // ── KML Import ────────────────────────────────────────────────────────────────

  const handleImportKML = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    try {
      const polygons = parseKmlFile(await file.text());
      if (polygons.length) setImportedPolygons(prev => [...prev, ...polygons]);
      else alert('No valid polygons found in KML file.');
    } catch { alert('Error importing KML file.'); }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDirectKMLImport = (polygons) => { if (polygons?.length) setImportedPolygons(prev => [...prev, ...polygons]); };

  const handleConvertToField = (polygon) => {
    const area = calculatePolygonArea(polygon.coordinates);
    setSelectedField(polygon);
    setNewFieldCoords(polygon.coordinates);
    setFieldForm({ name: polygon.name || `Poligon ${fields.length + 1}`, area: area.toFixed(2), tNumber: '', comuna: '', company: '', crop: '', cropType: '', stage: '', health: 'Good', contract: '', apiaOnly: false, folosinta: 'arabil' });
    setShowFieldForm(true);
  };

  const handleConvertAllPolygons = () => {
    if (!importedPolygons.length) return;
    if (window.confirm(`Convertești toate cele ${importedPolygons.length} poligoane importate?`)) {
      const now = Date.now();
      const nf = importedPolygons.map((p, i) => ({
        id: now + i, name: p.name || `Field ${fields.length + i + 1}`,
        area: calculatePolygonArea(p.coordinates).toFixed(2), coordinates: p.coordinates,
        tNumber: '', comuna: '', company: '', yearData: {}
      }));
      setFields(prev => [...prev, ...nf]);
      setImportedPolygons([]);
      localStorage.removeItem('agrimanage-imported');
    }
  };

  // ── Detect Comune ─────────────────────────────────────────────────────────────

  const detectComuneForExistingFields = async (forceAll = false) => {
    const toDetect = fields.filter(f =>
      f.coordinates?.length >= 3 && (forceAll || !f.comuna)
    );
    if (!toDetect.length) return;
    setDetectingComune(true);
    let updated = [...fields];
    for (const f of toDetect) {
      const lat = f.coordinates.reduce((s, p) => s + p.lat, 0) / f.coordinates.length;
      const lng = f.coordinates.reduce((s, p) => s + p.lng, 0) / f.coordinates.length;
      const det = await reverseGeocodeComuna(lat, lng);
      if (det) updated = updated.map(x => x.id === f.id ? { ...x, comuna: det } : x);
    }
    const foundAny = updated.some((x, i) => x.comuna !== fields[i]?.comuna);
    setFields(updated);
    setDetectingComune(false);
    if (!foundAny) alert('Nu s-a putut detecta nicio comună. Detectarea folosește Google Maps: verifică dacă facturarea Google Cloud este activă.');
  };

  // Arabil (not APIA-only, not pășune) — includes imported polygons
  const getTotalArabilArea = () => {
    const fromF = fields.filter(f => !f.apiaOnly && f.folosinta !== 'pasune')
                        .reduce((s, f) => s + (parseFloat(f.area) || 0), 0);
    const fromI = importedPolygons.reduce((s, p) => s + (parseFloat(p.area) || calculatePolygonArea(p.coordinates)), 0);
    return (fromF + fromI).toFixed(2);
  };

  // Pășune fields (not APIA-only)
  const getTotalPasune = () =>
    fields.filter(f => !f.apiaOnly && f.folosinta === 'pasune')
          .reduce((s, f) => s + (parseFloat(f.area) || 0), 0).toFixed(2);

  // Kept for backward compat in other places
  const getTotalWorkingArea = () => {
    const arabil = parseFloat(getTotalArabilArea());
    const pasune = parseFloat(getTotalPasune());
    return (arabil + pasune).toFixed(2);
  };

  // Fields declared only to APIA (not worked)
  const getTotalApiaArea = () =>
    fields.filter(f => f.apiaOnly).reduce((s, f) => s + (parseFloat(f.area) || 0), 0).toFixed(2);

  // Hectares per Comună (separated into worked vs APIA-only).
  // Groups by normalised key so "Mircești" and "Comuna Mircești" merge into one card.
  const comunaSummary = useMemo(() => {
    const m = {};
    fields.forEach(f => {
      const raw     = f.comuna || '(necunoscută)';
      const key     = normC(raw);
      const display = raw === '(necunoscută)' ? raw : canonicalComuna(raw);
      if (!m[key]) m[key] = { displayName: display, lucrat: 0, apia: 0 };
      const ha = parseFloat(f.area) || 0;
      if (f.apiaOnly) m[key].apia   += ha;
      else            m[key].lucrat += ha;
    });
    return Object.values(m)
      .map(({ displayName, lucrat, apia }) => ({ comuna: displayName, lucrat, apia, total: lucrat + apia }))
      .sort((a, b) => b.total - a.total);
  }, [fields]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Screen ────────────────────────────────────────────────────────────────────

  const mapFieldById = useMemo(() => {
    const byId = new Map();
    fieldsForMap.forEach(f => byId.set(f.id, f));
    importedPolygons.forEach(p => byId.set(p.id, p));
    return byId;
  }, [fieldsForMap, importedPolygons]);

  const currentField = selectedField ? (mapFieldById.get(selectedField.id) || null) : null;

  const filtersActive = Boolean(deferredSearch || filterCrop || filterStage || filterHealth || filterComuna);
  const dimmedIds = useMemo(() => {
    if (!filtersActive) return null;
    const matching = new Set(filteredFields.map(f => f.id));
    return new Set(fields.filter(f => !matching.has(f.id)).map(f => f.id));
  }, [filtersActive, filteredFields, fields]);

  const onMapSelect = useEvent((id) => {
    const field = mapFieldById.get(id);
    if (!field) return;
    if (batchMode) {
      handleFieldSelect(field);
    } else {
      setSelectedField(field);
      setSheet('half');
    }
  });
  const onMapBackground = useEvent(() => { if (!batchMode) setSelectedField(null); });
  const onSessionChange = useEvent((next) => setSession(next));

  const openFieldFromList = (field) => {
    if (batchMode) { handleFieldSelect(field); return; }
    const onMap = mapFieldById.get(field.id) || field;
    setSelectedField(onMap);
    mapRef.current?.focusField(onMap);
    setSheet('half');
  };

  const startDraw = () => {
    setSelectedField(null);
    mapRef.current?.startSession('draw');
  };

  const startSplit = (field) => {
    setSplitSourceField(field);
    setSplitMode(true);
    setSelectedField(null);
    mapRef.current?.focusField(field);
    mapRef.current?.startSession('split');
  };

  const cancelSession = () => {
    mapRef.current?.endSession();
    setEditingFieldId(null);
    setSplitMode(false);
    setSplitSourceField(null);
  };

  const finishSession = () => {
    const path = mapRef.current?.getSessionPath() || [];
    if (session?.mode === 'shape') { handleSaveShape(); return; }
    if (path.length < 3) return;
    const wasSplit = session?.mode === 'split';
    mapRef.current?.endSession();
    if (wasSplit) handleSplitDrawComplete(path);
    else handleDrawField(path);
  };

  const changeYear = (value) => {
    if (value === '__new') setShowAddYear(true);
    else setSelectedYear(value);
  };

  // Sheet: dragging the handle on a phone. Moves the element directly, without React renders.
  const startSheetDrag = (event) => {
    const element = sheetRef.current;
    if (!element || !window.matchMedia('(max-width: 900px)').matches) return;
    const startY = event.clientY;
    const startTop = element.getBoundingClientRect().top - element.parentElement.getBoundingClientRect().top;
    let moved = false;
    const move = (e) => {
      const delta = e.clientY - startY;
      if (Math.abs(delta) > 4) moved = true;
      element.style.transition = 'none';
      element.style.transform = `translateY(${Math.max(8, startTop + delta)}px)`;
    };
    const end = (e) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      element.style.transition = '';
      element.style.transform = '';
      if (!moved) { setSheet(s => (s === 'peek' ? 'half' : s === 'half' ? 'full' : 'peek')); return; }
      const height = element.parentElement.clientHeight;
      const position = (startTop + e.clientY - startY) / height;
      setSheet(position < 0.28 ? 'full' : position < 0.72 ? 'half' : 'peek');
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
  };

  const totalAll = (parseFloat(getTotalArabilArea()) + parseFloat(getTotalPasune()) + parseFloat(getTotalApiaArea())).toFixed(2);
  const cropRows = Object.entries(cropHa).sort(([, a], [, b]) => b - a);
  const cropTotal = cropRows.reduce((sum, [, ha]) => sum + ha, 0);
  const missingComune = fields.filter(f => f.coordinates?.length >= 3 && !f.comuna).length;
  const withShape = fields.filter(f => f.coordinates?.length >= 3).length;

  // Fields and parcels per comună, and per T inside each comună: a T only means
  // something inside its comună. Fields without a T still count in the comună's total.
  const comparisonByComuna = (() => {
    const communes = {};
    const entry = (name) => {
      const display = name ? canonicalComuna(name) : 'Fără comună';
      const key = name ? normC(name) : '__none';
      if (!communes[key]) communes[key] = { key, comuna: display, haFields: 0, haParcels: 0, fieldCount: 0, parcelCount: 0, ts: {} };
      return communes[key];
    };
    const tEntry = (c, t) => {
      if (!c.ts[t]) c.ts[t] = { t, haFields: 0, haParcels: 0 };
      return c.ts[t];
    };
    fields.forEach(f => {
      const c = entry(f.comuna);
      const ha = parseFloat(f.area) || 0;
      c.haFields += ha; c.fieldCount += 1;
      if (f.tNumber) tEntry(c, normT(f.tNumber)).haFields += ha;
    });
    parcelsData.forEach(p => {
      const c = entry(p.locatie);
      const ha = parseFloat(p.suprafata) || 0;
      c.haParcels += ha; c.parcelCount += 1;
      if (p.t !== undefined && p.t !== null && p.t !== '') tEntry(c, normT(p.t)).haParcels += ha;
    });
    return Object.values(communes)
      .map(c => ({ ...c, diff: c.haFields - c.haParcels, ts: Object.values(c.ts).sort((x, y) => Number(x.t) - Number(y.t)) }))
      .sort((x, y) => y.haFields - x.haFields);
  })();

  const yd = currentField && !currentField.imported ? getFieldYearData(currentField, selectedYear) : null;
  const inSession = Boolean(session);

  return (
    <div className={`fp ${inSession ? 'is-session' : ''}`}>
      <div className="fp-map">
        <FieldMap
          ref={mapRef}
          fields={fieldsForMap}
          importedPolygons={importedPolygons}
          selectedId={currentField?.id ?? null}
          dimmedIds={dimmedIds}
          hiddenId={editingFieldId}
          alertId={splitSourceField?.id ?? null}
          onSelect={onMapSelect}
          onBackgroundClick={onMapBackground}
          onImportKML={handleDirectKMLImport}
          onSessionChange={onSessionChange}
          onMapReady={onMapReady}
        />

        {!inSession && (
          <div className="fp-top">
            <div className="fp-search">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#56635B" strokeWidth="2" aria-hidden="true"><path d="M11 18a7 7 0 100-14 7 7 0 000 14zM20 20l-4-4" /></svg>
              <input type="search" aria-label="Caută câmpuri" placeholder="Caută câmp, cultură, contract"
                value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
              <select className="fp-year" aria-label="An agricol" value={selectedYear} onChange={e => changeYear(e.target.value)}>
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                <option value="__new">+ An nou</option>
              </select>
            </div>
            <div className="fp-chips">
              <label className={`fp-chip ${filterCrop ? 'is-on' : ''}`}>
                <select aria-label="Cultură" value={filterCrop} onChange={e => setFilterCrop(e.target.value)}>
                  <option value="">Cultură</option>
                  {cropTypes.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </label>
              <label className={`fp-chip ${filterStage ? 'is-on' : ''}`}>
                <select aria-label="Stadiu" value={filterStage} onChange={e => setFilterStage(e.target.value)}>
                  <option value="">Stadiu</option>
                  {stageOptions.map(s => <option key={s} value={s}>{DISPLAY[s] || s}</option>)}
                </select>
              </label>
              <label className={`fp-chip ${filterHealth ? 'is-on' : ''}`}>
                <select aria-label="Sănătate" value={filterHealth} onChange={e => setFilterHealth(e.target.value)}>
                  <option value="">Sănătate</option>
                  {healthOptions.map(h => <option key={h} value={h}>{DISPLAY[h] || h}</option>)}
                </select>
              </label>
              <label className={`fp-chip ${filterComuna ? 'is-on' : ''}`}>
                <select aria-label="Comună" value={filterComuna} onChange={e => setFilterComuna(e.target.value)}>
                  <option value="">Comună</option>
                  {comunaSummary.map(c => <option key={c.comuna} value={c.comuna}>{c.comuna}</option>)}
                </select>
              </label>
              {filtersActive && <button type="button" className="fp-chip-clear" onClick={clearFilters}>Șterge filtre</button>}
            </div>
          </div>
        )}

        {inSession && (
          <>
            <div className="fp-session-top">
              <div>
                <strong>{session.points >= 3 ? `${session.area.toFixed(2)} ha` : session.mode === 'split' ? 'Taie câmpul' : 'Câmp nou'}</strong>
                <span>
                  {session.mode === 'shape' && `Editezi forma · ${session.points} puncte`}
                  {session.mode === 'draw' && `${session.points} puncte · cel puțin 3`}
                  {session.mode === 'split' && `Porțiunea de tăiat · ${session.points} puncte`}
                </span>
              </div>
              <button type="button" onClick={cancelSession}>Anulează</button>
            </div>
            <div className="fp-session">
              <p>
                {session.mode === 'shape'
                  ? 'Trage punctele albe. Atinge un punct verde pentru a adăuga unul nou.'
                  : 'Mută harta sub cruce și apasă Adaugă punct, sau atinge direct harta.'}
              </p>
              {session.mode !== 'shape' && (
                <button type="button" className="fp-session-add" onClick={() => mapRef.current?.addPointAtCenter()}>+ Adaugă punct</button>
              )}
              <div className="fp-session-row">
                <button type="button" disabled={!session.canUndo} onClick={() => mapRef.current?.undo()}>Înapoi un pas</button>
                {session.hasSelectedPoint ? (
                  <button type="button" onClick={() => mapRef.current?.deleteSelectedPoint()}>Șterge punctul</button>
                ) : session.mode !== 'shape' ? (
                  <button type="button" disabled={!session.points} onClick={() => mapRef.current?.clearPoints()}>Șterge tot</button>
                ) : null}
                <button type="button" className="is-primary" disabled={session.points < 3} onClick={finishSession}>
                  {session.mode === 'split' ? 'Aplică tăierea' : session.mode === 'shape' ? 'Finalizează' : 'Salvează'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {!inSession && (
        <aside className={`fp-sheet is-${sheet}`} ref={sheetRef}>
          <button type="button" className="fp-handle" aria-label="Mărește sau micșorează panoul" onPointerDown={startSheetDrag}><span /></button>

          {currentField ? (
            <div className="fp-detail">
              <div className="fp-detail-head">
                <button type="button" className="fp-back" aria-label="Înapoi la listă" onClick={() => setSelectedField(null)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
                </button>
                <div className="fp-detail-title">
                  <h2>{currentField.name || 'Fără nume'}</h2>
                  <p>{[currentField.tNumber && `T${normT(currentField.tNumber)}`, currentField.comuna, currentField.company].filter(Boolean).join(' · ') || (currentField.imported ? 'Importat din KML' : '')}</p>
                </div>
                <div className="fp-detail-area">
                  <strong>{currentField.imported ? calculatePolygonArea(currentField.coordinates).toFixed(2) : currentField.area}</strong>
                  <span>hectare</span>
                </div>
              </div>

              {currentField.imported ? (
                <button type="button" className="fp-primary" onClick={() => handleConvertToField(currentField)}>Convertește în câmp</button>
              ) : (
                <>
                  <div className="fp-actions">
                    <button type="button" className="is-primary" onClick={() => handleEditField(currentField)}><Icon d="M4 20h4L19 9l-4-4L4 16zM13 7l4 4" />Editează</button>
                    {currentField.coordinates?.length >= 3 && <button type="button" onClick={() => handleStartEditShape(currentField)}><Icon d="M5 7l7-4 7 5-2 10-9 2-4-7z" />Formă</button>}
                    {currentField.coordinates?.length >= 3 && <button type="button" onClick={() => startSplit(currentField)}><Icon d="M6 6l12 12M18 6l-5 5M9 15l-3 3" />Taie</button>}
                    <button type="button" className="is-danger" onClick={() => handleDeleteField(currentField.id)}><Icon d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />Șterge</button>
                  </div>
                  <h3 className="fp-subhead">An agricol {selectedYear}</h3>
                  <dl className="fp-rows">
                    <div><dt>Cultură</dt><dd>{yd.crop || '—'}</dd></div>
                    <div><dt>Stadiu</dt><dd>{DISPLAY[yd.stage] || yd.stage || '—'}</dd></div>
                    <div><dt>Sănătate</dt><dd>{DISPLAY[yd.health] || yd.health || '—'}</dd></div>
                    <div><dt>Contract</dt><dd>{yd.contract || '—'}</dd></div>
                    <div><dt>Folosință</dt><dd>{currentField.folosinta === 'pasune' ? 'Pășune' : 'Arabil'}{currentField.apiaOnly ? ' · doar APIA' : ''}</dd></div>
                  </dl>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="fp-summary">
                <div>
                  <strong>{totalAll} ha</strong>
                  <span>{fields.length} câmpuri · {getTotalArabilArea()} arabil{parseFloat(getTotalPasune()) > 0 ? ` · ${getTotalPasune()} pășune` : ''}</span>
                </div>
                <button type="button" className="fp-add" onClick={startDraw}>+ Câmp</button>
              </div>
              <div className="fp-cropbar" aria-hidden="true">
                {cropRows.map(([ct, ha]) => <span key={ct} style={{ flex: ha, background: cropTypes.find(c => c.value === ct)?.color || '#DDD' }} />)}
                {cropTotal === 0 && <span style={{ flex: 1, background: '#E2E6E0' }} />}
              </div>

              <div className="fp-tabs" role="tablist">
                {[['list', 'Câmpuri'], ['stats', 'Rezumat'], ['tools', 'Unelte']].map(([id, label]) => (
                  <button key={id} type="button" role="tab" aria-selected={panelTab === id} className={panelTab === id ? 'is-on' : ''}
                    onClick={() => { setPanelTab(id); if (sheet === 'peek') setSheet('half'); }}>{label}</button>
                ))}
              </div>

              <div className="fp-scroll">
                {panelTab === 'list' && (
                  <>
                    <div className="fp-listbar">
                      <span>{filteredFields.length !== fields.length ? `${filteredFields.length} din ${fields.length}` : `${fields.length} câmpuri`}</span>
                      <label>Sortează
                        <select value={sortConfig.key} onChange={e => setSortConfig(prev => ({ ...prev, key: e.target.value }))}>
                          <option value="name">Nume</option>
                          <option value="area">Suprafață</option>
                          <option value="tNumber">T</option>
                          <option value="comuna">Comună</option>
                          <option value="company">Firmă</option>
                          <option value="crop">Cultură</option>
                          <option value="stage">Stadiu</option>
                          <option value="health">Sănătate</option>
                          <option value="contract">Contract</option>
                        </select>
                      </label>
                      <button type="button" aria-label={sortConfig.direction === 'ascending' ? 'Sortare crescătoare' : 'Sortare descrescătoare'}
                        onClick={() => setSortConfig(prev => ({ ...prev, direction: prev.direction === 'ascending' ? 'descending' : 'ascending' }))}>
                        {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                      </button>
                    </div>

                    {batchMode && (
                      <div className="fp-batch">
                        <span>{selectedFields.length} selectate</span>
                        <button type="button" onClick={toggleSelectAllFields}>{selectedFields.length === filteredFields.length && filteredFields.length > 0 ? 'Deselectează' : 'Selectează tot'}</button>
                        <button type="button" className="is-danger" disabled={!selectedFields.length} onClick={handleBatchDelete}>Șterge</button>
                        <button type="button" onClick={toggleBatchMode}>Gata</button>
                      </div>
                    )}

                    {filteredFields.map(field => {
                      const y = getFieldYearData(field, selectedYear);
                      const crop = cropTypes.find(c => c.value === y.cropType);
                      const checked = batchMode && selectedFields.some(f => f.id === field.id);
                      return (
                        <button key={field.id} type="button" className={`fp-row ${checked ? 'is-checked' : ''}`} onClick={() => openFieldFromList(field)} aria-pressed={batchMode ? checked : undefined}>
                          {batchMode && <span className="fp-check" aria-hidden="true">{checked ? '✓' : ''}</span>}
                          <span className="fp-dot" style={{ background: crop?.color || (field.folosinta === 'pasune' ? '#2D6A2D' : '#C5CCC4') }} />
                          <span className="fp-row-main">
                            <strong>{field.name}{field.apiaOnly && <em>APIA</em>}</strong>
                            <span>{[field.tNumber && `T${normT(field.tNumber)}`, y.crop, field.comuna].filter(Boolean).join(' · ') || '—'}</span>
                          </span>
                          <span className="fp-row-area">{field.area} ha</span>
                        </button>
                      );
                    })}

                    {importedPolygons.map(polygon => (
                      <button key={polygon.id} type="button" className="fp-row" onClick={() => openFieldFromList(polygon)}>
                        <span className="fp-dot" style={{ background: '#F39C12' }} />
                        <span className="fp-row-main">
                          <strong>{polygon.name || 'Importat'}</strong>
                          <span>Importat din KML</span>
                        </span>
                        <span className="fp-row-area">{calculatePolygonArea(polygon.coordinates).toFixed(2)} ha</span>
                      </button>
                    ))}

                    {filteredFields.length === 0 && importedPolygons.length === 0 && (
                      <div className="fp-empty">
                        {fields.length > 0 ? 'Niciun câmp nu corespunde filtrelor.' : 'Nu există câmpuri încă. Desenează primul câmp pe hartă sau importă un fișier KML.'}
                        {fields.length > 0 && <button type="button" onClick={clearFilters}>Șterge filtrele</button>}
                      </div>
                    )}
                  </>
                )}

                {panelTab === 'stats' && (
                  <>
                    <section className="fp-card">
                      <h3>Total suprafață</h3>
                      <dl className="fp-rows">
                        <div><dt>Arabil</dt><dd>{getTotalArabilArea()} ha</dd></div>
                        {parseFloat(getTotalPasune()) > 0 && <div><dt>Pășune</dt><dd>{getTotalPasune()} ha</dd></div>}
                        {parseFloat(getTotalApiaArea()) > 0 && <div><dt>Doar APIA</dt><dd>{getTotalApiaArea()} ha</dd></div>}
                        <div><dt>Total</dt><dd>{totalAll} ha</dd></div>
                        <div><dt>Câmpuri</dt><dd>{fields.length}</dd></div>
                      </dl>
                    </section>
                    <section className="fp-card">
                      <h3>Pe cultură, {selectedYear}</h3>
                      {cropRows.length === 0 && <p className="fp-muted">Nicio cultură setată.</p>}
                      {cropRows.map(([ct, ha]) => {
                        const crop = cropTypes.find(c => c.value === ct);
                        return <div key={ct} className="fp-crop"><span className="fp-dot" style={{ background: crop?.color || '#DDD' }} /><span>{crop?.label || ct}</span><strong>{ha.toFixed(2)} ha</strong></div>;
                      })}
                    </section>
                    {comunaSummary.length > 0 && (
                      <section className="fp-card">
                        <h3>Pe comună</h3>
                        <p className="fp-muted">Apasă pe o comună pentru a filtra câmpurile.</p>
                        <div className="fp-comune">
                          {comunaSummary.map(({ comuna, lucrat, apia }) => {
                            const isActive = filterComuna && normC(filterComuna) === normC(comuna);
                            return (
                              <button key={comuna} type="button" className={isActive ? 'is-on' : ''} aria-pressed={Boolean(isActive)} onClick={() => setFilterComuna(isActive ? '' : comuna)}>
                                <strong>{comuna}</strong>
                                <span>{lucrat.toFixed(2)} ha{apia > 0 ? ` + ${apia.toFixed(2)} APIA` : ''}</span>
                              </button>
                            );
                          })}
                        </div>
                      </section>
                    )}
                    {comparisonByComuna.length > 0 && (
                      <section className="fp-card">
                        <h3>Câmpuri și parcele, pe comună</h3>
                        <p className="fp-muted">Suprafața câmpurilor de pe hartă comparată cu Manager Parcele. Atinge o comună pentru totalurile pe T.</p>
                        {comparisonByComuna.map((c) => (
                          <details key={c.key} className="fp-comuna">
                            <summary>
                              <span className="fp-comuna-name"><strong>{c.comuna}</strong><span>{c.fieldCount} câmpuri · {c.parcelCount} parcele</span></span>
                              <span className="fp-comuna-nums">
                                <span>{c.haFields.toFixed(2)} ha hărți</span>
                                <span>{c.haParcels.toFixed(2)} ha parcele</span>
                                <strong className={Math.abs(c.diff) < 0.01 ? 'is-ok' : c.diff > 0 ? 'is-over' : 'is-under'}>{c.diff > 0 ? '+' : ''}{c.diff.toFixed(2)}</strong>
                              </span>
                            </summary>
                            {c.ts.length > 0 ? (
                              <div className="fp-table-wrap">
                                <table className="fp-table">
                                  <thead><tr><th>T</th><th>Hărți</th><th>Parcele</th><th>Diferență</th></tr></thead>
                                  <tbody>
                                    {c.ts.map(({ t, haFields, haParcels }) => {
                                      const diff = haFields - haParcels;
                                      return (
                                        <tr key={t}>
                                          <td>T{t}</td><td>{haFields.toFixed(2)}</td><td>{haParcels.toFixed(2)}</td>
                                          <td className={Math.abs(diff) < 0.01 ? 'is-ok' : diff > 0 ? 'is-over' : 'is-under'}>{diff > 0 ? '+' : ''}{diff.toFixed(2)}{Math.abs(diff) < 0.01 ? ' ✓' : ''}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            ) : <p className="fp-muted">Niciun T completat în această comună.</p>}
                          </details>
                        ))}
                      </section>
                    )}
                  </>
                )}

                {panelTab === 'tools' && (
                  <div className="fp-tools-list">
                    <button type="button" onClick={startDraw}>Desenează un câmp nou</button>
                    <button type="button" onClick={() => fileInputRef.current?.click()}>Importă fișier KML</button>
                    {importedPolygons.length > 0 && <button type="button" onClick={handleConvertAllPolygons}>Convertește importate ({importedPolygons.length})</button>}
                    <button type="button" onClick={() => { toggleBatchMode(); setPanelTab('list'); setSheet('full'); }}>{batchMode ? 'Ieși din selecție' : 'Selecție multiplă'}</button>
                    {missingComune > 0 && (
                      <button type="button" disabled={detectingComune} onClick={() => detectComuneForExistingFields(false)}>
                        {detectingComune ? 'Se detectează…' : `Detectează comune (${missingComune})`}
                      </button>
                    )}
                    {withShape > 0 && (
                      <button type="button" disabled={detectingComune} onClick={() => {
                        if (window.confirm(`Re-detectează Comună pentru TOATE câmpurile (${withShape})?\nAcest lucru va suprascrie comunele deja setate.`)) detectComuneForExistingFields(true);
                      }}>
                        {detectingComune ? 'Se detectează…' : 'Re-detectează toate comunele'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </aside>
      )}

      <input type="file" accept=".kml" ref={fileInputRef} style={{ display: 'none' }} onChange={handleImportKML} />

      {showAddYear && (
        <div className="fp-overlay" onClick={e => e.target === e.currentTarget && setShowAddYear(false)}>
          <div className="fp-dialog" role="dialog" aria-modal="true" aria-label="An agricol nou">
            <h2>An agricol nou</h2>
            <label>An
              <input autoFocus value={newYearInput} onChange={e => setNewYearInput(e.target.value)} placeholder="ex. 2026-2027" onKeyDown={e => e.key === 'Enter' && handleAddYear()} />
            </label>
            <div className="fp-form-actions">
              <button type="button" onClick={() => setShowAddYear(false)}>Anulează</button>
              <button type="button" className="is-primary" onClick={handleAddYear}>Adaugă</button>
            </div>
          </div>
        </div>
      )}

      {showFieldForm && (
        <div className="fp-overlay">
          <form className="fp-form" onSubmit={e => { e.preventDefault(); handleSaveField(); }}>
            <h2>{newFieldCoords ? 'Câmp nou' : 'Editează câmpul'}</h2>
            <div className="fp-form-body">
              <label>Nume câmp<input type="text" name="name" value={fieldForm.name} onChange={handleFormChange} required /></label>
              <div className="fp-form-2">
                <label>Suprafață (ha)<input type="text" name="area" value={fieldForm.area} onChange={handleFormChange} disabled={newFieldCoords !== null} /></label>
                <label>Număr T<input type="text" name="tNumber" value={fieldForm.tNumber} onChange={handleFormChange} placeholder="ex. 63" /></label>
              </div>
              <label>Comună
                {newFieldCoords && comunaDetection === 'searching' && !fieldForm.comuna && <small> se detectează…</small>}
                {newFieldCoords && comunaDetection === 'failed' && !fieldForm.comuna && <small> nu s-a putut detecta automat, scrie-o aici</small>}
                <input type="text" name="comuna" value={fieldForm.comuna} onChange={handleFormChange} />
              </label>
              <label>Firmă
                {companies.length > 0 ? (
                  <select name="company" value={fieldForm.company} onChange={handleFormChange}>
                    <option value="">Fără firmă</option>
                    {companies.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                ) : (
                  <input type="text" name="company" value={fieldForm.company} onChange={handleFormChange} placeholder="Denumire firmă" />
                )}
              </label>
              <label>Categorie de folosință
                <select name="folosinta" value={fieldForm.folosinta || 'arabil'} onChange={handleFormChange}>
                  <option value="arabil">Arabil</option>
                  <option value="pasune">Pășune</option>
                </select>
              </label>
              <label className="fp-form-check">
                <input type="checkbox" name="apiaOnly" checked={Boolean(fieldForm.apiaOnly)} onChange={handleFormChange} />
                <span>Doar declarat APIA, nu se lucrează<small>Suprafața nu intră în totalul hectarelor lucrate.</small></span>
              </label>

              <h3 className="fp-subhead">Date pentru {selectedYear}</h3>
              <label>Tip cultură
                <select name="cropType" value={fieldForm.cropType || ''} onChange={handleFormChange}>
                  <option value="">Selectați cultura</option>
                  {cropTypes.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </label>
              <div className="fp-form-2">
                <label>Stadiu
                  <select name="stage" value={fieldForm.stage || ''} onChange={handleFormChange}>
                    <option value="">Selectați</option>
                    <option value="Planting">Plantare</option>
                    <option value="Growing">Creștere</option>
                    <option value="Harvesting">Recoltare</option>
                    <option value="Harvested">Recoltat</option>
                  </select>
                </label>
                <label>Sănătate
                  <select name="health" value={fieldForm.health || 'Good'} onChange={handleFormChange}>
                    <option value="Excellent">Excelent</option>
                    <option value="Good">Bun</option>
                    <option value="Fair">Acceptabil</option>
                    <option value="Poor">Slab</option>
                    <option value="N/A">N/A</option>
                  </select>
                </label>
              </div>
              <label>Contract<input type="text" name="contract" value={fieldForm.contract || ''} onChange={handleFormChange} placeholder="Detalii contract sau arendă" /></label>
            </div>
            <div className="fp-form-actions">
              <button type="button" onClick={handleCancelField}>Anulează</button>
              <button type="submit" className="is-primary">Salvează</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Fields;
